import apiHelper from './apiHelper';

// Module-level singletons — shared across every component that imports this file.
let _cache = null;    // resolved array, null means "not yet fetched"
let _promise = null;  // in-flight fetch, null when idle

async function fetchSpuNames() {
    // Try the lightweight endpoint first; fall back to the full list if unavailable.
    try {
        const res = await apiHelper('inventory/spus/names/');
        if (res?.ok) {
            const data = await res.json();
            if (Array.isArray(data) && data.length > 0) return data;
        }
    } catch (_) { /* ignore, try fallback */ }

    // Fallback: use the existing endpoint (works even before backend restart).
    try {
        const res = await apiHelper('inventory/spus/?archived=false');
        if (res?.ok) {
            const data = await res.json();
            return Array.isArray(data) ? data : [];
        }
    } catch (_) { /* ignore */ }

    return [];
}

/**
 * Fire-and-forget prefetch. Safe to call multiple times — only one request
 * ever runs. Call this when the SPU list page mounts so the data is ready
 * before the user clicks "Add SPU".
 */
export function prefetchSpuNames() {
    if (_cache !== null || _promise !== null) return;
    _promise = fetchSpuNames().then(data => {
        _cache = data;
        _promise = null;
    });
}

/**
 * Returns the cache synchronously. null = not yet loaded.
 * Use this to initialise useState so the form gets data immediately
 * when the cache is already warm.
 */
export function getSpuNamesCache() {
    return _cache;
}

/**
 * Returns a Promise that resolves to the cache array.
 * - If already loaded  → resolves immediately (next microtask).
 * - If fetch in flight → resolves when that fetch finishes.
 * - If nothing started → kicks off the fetch then resolves.
 */
export function ensureSpuNamesLoaded() {
    if (_cache !== null) return Promise.resolve(_cache);
    if (_promise !== null) return _promise.then(() => _cache);
    prefetchSpuNames();
    return _promise.then(() => _cache);
}

/**
 * Call after creating or archiving an SPU so the next form open re-fetches.
 */
export function invalidateSpuNamesCache() {
    _cache = null;
    _promise = null;
}
